import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Flex, Form, Layout, Radio } from "antd";
import Highcharts from "highcharts";

import { GET, POST } from "../../../providers/useAxiosQuery";
import FloatInput from "../../../providers/FloatInput";
import ScorecardGraph from "../../../providers/ScorecardGraph";

require("highcharts/highcharts-more")(Highcharts);
require("highcharts/modules/accessibility")(Highcharts);
require("highcharts/modules/variable-pie")(Highcharts);
require("highcharts/modules/no-data-to-display")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/offline-exporting")(Highcharts);
require("highcharts/modules/export-data")(Highcharts);
require("highcharts/modules/boost")(Highcharts);

export default function PageScoreCardFormSuccess() {
	const navigate = useNavigate();

	const [form] = Form.useForm();
	const [shareNowStatus, setShareNowStatus] = useState(0);
	const [errMessage, setErrMessage] = useState({
		type: "",
		message: "",
	});

	const [graphResult, setGraphResult] = useState([]);
	const [firstname, setFirstname] = useState("");

	GET(
		`api/scorecard_result?batch_id=${localStorage.getItem(
			"scorecard_batch_id"
		)}`,
		"scorecard_result",
		async (res) => {
			if (res && res.data) {
				let data = res.data;
				let findForm = data[0];

				if (findForm) {
					let form_question_answer_batches =
						findForm.form_question_answer_batches;
					let form_question_answer_batch = form_question_answer_batches.length
						? form_question_answer_batches[0]
						: null;
					let form_question_answers =
						form_question_answer_batch?.form_question_answers || [];

					setFirstname(form_question_answer_batch?.user?.firstname);

					let pluckedCategories = form_question_answers.reduce(
						(acc, answer) => {
							let form_question_category_id = answer.form_question_category_id;
							if (!acc.includes(form_question_category_id)) {
								acc.push(form_question_category_id);
							}
							return acc;
						},
						[]
					);

					let form_question_categories = findForm.form_question_categories
						?.filter((category) => pluckedCategories.includes(category.id))
						.filter((category) => category.graph_visible === 1);

					let categories = [];

					if (form_question_answer_batch) {
						form_question_categories.forEach((category) => {
							let filter_form_question_answers = form_question_answers.filter(
								(answer) => answer.form_question_category_id === category.id
							);

							let countAnswered = filter_form_question_answers.reduce(
								(acc, answer) =>
									acc + (answer.answer ? Number(answer.answer) * 5 : 0),
								0
							);
							countAnswered =
								countAnswered > 0 && filter_form_question_answers.length
									? countAnswered / filter_form_question_answers.length
									: countAnswered;

							categories.push({
								name: category.category,
								y: countAnswered,
								color: category.graph_color,
							});
						});
					}

					setGraphResult(categories);
				}
			}
		},
		false
	);

	const { mutate: mutateUser, isLoading: isLoadingUser } = POST(
		`api/scorecard_email_share_pub`,
		"scorecard_email_share_pub"
	);

	const onFinish = (values) => {
		console.log("message: ", values);
		console.log("graphResult: ", graphResult);

		let data = {
			fullname: values.fullname ?? "",
			phone_share: values.contact_no ?? "",
			email_share: values.email ?? "",
			share_status: shareNowStatus === 2 ? "Share in 5 minutes" : "Share Now",
			batch_id: localStorage.getItem("scorecard_batch_id") || "",
			link: window.location.origin,
			scorecard_data: graphResult,
			relationship:
				localStorage.getItem("scorecardRelationship") === "Single"
					? "friend"
					: "partner",
		};
		mutateUser(data, {
			onSuccess: (res) => {
				if (res.success) {
					setErrMessage({
						type: "success",
						message:
							shareNowStatus === 2
								? "Email send in 5 minutes"
								: "Email sent successfully!",
					});

					setTimeout(() => {
						navigate("/scorecard/form/thank-you");
					}, 1000);
				} else {
					setErrMessage({
						type: "error",
						message: res.message,
					});
				}
			},
			onError: (err) => {
				let errors = err.response.data.errors;
				if (errors) {
					let fieldnames = Object.keys(errors);
					Object.values(errors).map((messages, index) => {
						let fieldname = fieldnames[index].split("_");
						fieldname.map((string, key) => {
							fieldname[key] = capitalize(string);

							return false;
						});
						fieldname = fieldname.join(" ");

						setErrMessage({
							type: "error",
							message: messages.join("\n\r"),
						});

						return false;
					});
				} else {
					setErrMessage({
						type: "error",
						message: err.code + ": " + err.message,
					});
				}
			},
		});
	};

	const capitalize = (s) => {
		if (typeof s !== "string") return "";
		return s.charAt(0).toUpperCase() + s.slice(1);
	};

	return (
		<Layout.Content>
			<div className="main-content">
				<div className="logo-wrapper">
					<img
						src="/assets/joyalifevertical_green_on_white_transparent.png"
						alt="joyalifevertical_green_on_white_transparent.png"
					/>
				</div>

				<div className="content-title">
					Thank you! Your Joya Life Scorecard is on its way!
				</div>

				<div className="content-description text-center">
					We've emailed your personalised life wheel and some gentle reflections
					to support you on your journey. Check your inbox for your scorecard.{" "}
					<i>
						{`(If you don't see in in a few minutes, check your spam or promotions
						folder.)`}
					</i>
				</div>

				<div className="graph-wrapper">
					<ScorecardGraph
						Highcharts={Highcharts}
						graphResult={graphResult}
						firstname={firstname}
					/>
				</div>

				<Form form={form} onFinish={onFinish}>
					<div className="form-info">
						Would you like your{" "}
						<span className="highlight">
							{localStorage.getItem("scorecardRelationship") === "Single"
								? "friend"
								: "partner"}
						</span>{" "}
						to complete their scorecard?
					</div>

					<Form.Item
						name="complete_survey"
						rules={[{ required: true, message: "This field is required" }]}
					>
						<Radio.Group
							block
							optionType="button"
							buttonStyle="solid"
							style={{ gap: 10 }}
							onChange={(e) => {
								if (e.target.value === "NO") {
									navigate("/scorecard/form/thank-you");
								}
							}}
						>
							<Radio value="YES">YES</Radio>
							<Radio value="NO">NO</Radio>
						</Radio.Group>
					</Form.Item>

					<Form.Item shouldUpdate noStyle>
						{({ getFieldValue }) => {
							let complete_survey = getFieldValue("complete_survey");
							let communication = getFieldValue("communication");

							if (complete_survey === "YES") {
								return (
									<>
										<Form.Item
											name="communication"
											rules={[
												{ required: true, message: "This field is required" },
											]}
										>
											<Radio.Group
												block
												optionType="button"
												buttonStyle="solid"
												style={{ gap: 10 }}
											>
												<Radio value="SMS">SMS</Radio>
												<Radio value="EMAIL">EMAIL</Radio>
											</Radio.Group>
										</Form.Item>
										<Form.Item
											name="fullname"
											rules={[
												{
													required: true,
													message: "This field is required",
												},
											]}
										>
											<FloatInput
												label="Name"
												placeholder="Enter name"
												required
											/>
										</Form.Item>
										{communication ? (
											communication === "SMS" ? (
												<Form.Item
													name="contact_no"
													rules={[
														{
															required: true,
															message: "This field is required",
														},
													]}
												>
													<FloatInput
														label="Mobile Number"
														placeholder="Enter mobile number"
														required
													/>
												</Form.Item>
											) : (
												<Form.Item
													name="email"
													rules={[
														{
															required: true,
															message: "This field is required",
														},
														{
															type: "email",
															message: "Please enter a valid email address",
														},
													]}
												>
													<FloatInput
														label="Email Address"
														placeholder="Enter email address"
														required
													/>
												</Form.Item>
											)
										) : null}
										<Flex
											align="center"
											justify="center"
											className="flex-share-now"
										>
											<Flex gap={10} align="center" justify="center">
												<Button
													className="w-50-5"
													onClick={() => {
														form.submit();

														setShareNowStatus(1);
													}}
													loading={isLoadingUser}
												>
													SHARE NOW
												</Button>
												<Button
													className="w-50-5"
													onClick={() => {
														form.submit();

														setShareNowStatus(2);
													}}
													loading={isLoadingUser}
												>
													SHARE IN 5 MINUTES
												</Button>
											</Flex>
										</Flex>
										{errMessage.message && (
											<Alert
												type={errMessage.type}
												message={errMessage.message}
											/>
										)}
									</>
								);
							}
						}}
					</Form.Item>
				</Form>
			</div>

			<div className="footer">
				<div>
					<i>Disclaimer:</i>
				</div>
				<br />
				<div>
					<i>
						Nothing in the scorecard is meant to harm, nor provide a specific
						diagnosis or advice at this stage. If you need help, please speak to
						your GP or appropriate expert to get a referral to a qualified
						professional.
					</i>
				</div>
				<br />
				<div className="emergency">
					If you require urgent assistance, please contact lifeline on 13 11 14
					within Australia, or look up your local equivalent outside of
					Australia.
				</div>
			</div>
		</Layout.Content>
	);
}
